import { Dialog } from "@base-ui/react/dialog";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * A panel against an edge of the window, with the app taken out of play
 * behind it.
 *
 * It is a dialog in every way that matters — it takes the keyboard, Escape
 * and the scrim close it, and nothing behind it can be reached — so it is one,
 * and only its arrival is its own. That arrival changes sides at 40rem: from
 * the right on a wide screen, and up from the foot on a narrow one, where a
 * sidebar would be the whole display. Give `className` geometry that changes
 * sides at the same width.
 */
export function EdgePanel({
  open,
  onOpenChange,
  title,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What a screen reader calls the panel. Its visible heading is its own. */
  title: string;
  /** Where the panel sits, in the frame of whatever is opening it. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="edge-panel-scrim fixed inset-0 z-50" />
        <Dialog.Popup
          className={cn(
            "edge-panel fixed z-50 flex flex-col bg-popover text-popover-foreground ring-1 ring-foreground/10 outline-none",
            className,
          )}
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
